/** @format */

import React from "react";
import {
	Box,
	Card,
	CardContent,
	Container,
	Typography,
	Button,
	Stack,
	Link as MuiLink,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import HomeIcon from "@mui/icons-material/Home";
import { colors } from "../../utility/types/constant";

interface AccountDeletedState {
	deletedAt?: string;
	reason?: string;
}

const AccountDeleted: React.FC = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const state = location.state as AccountDeletedState;

	const deletedAt = state?.deletedAt
		? new Date(state.deletedAt).toLocaleDateString("en-US", {
				year: "numeric",
				month: "long",
				day: "numeric",
		  })
		: null;

	const reason = state?.reason || "Account deleted by administrator";

	return (
		<Box
			sx={{
				minHeight: "100vh",
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				background: colors.primary,
				padding: 3,
			}}>
			<Container maxWidth="sm">
				<Card
					elevation={8}
					sx={{
						borderRadius: 3,
						overflow: "hidden",
					}}>
					<Box
						sx={{
							background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
							padding: 4,
							textAlign: "center",
						}}>
						<ErrorOutlineIcon
							sx={{
								fontSize: 80,
								color: "white",
								mb: 2,
							}}
						/>
						<Typography
							variant="h4"
							component="h1"
							sx={{
								color: "white",
								fontWeight: 700,
								mb: 1,
							}}>
							Account Deleted
						</Typography>
						<Typography
							variant="subtitle1"
							sx={{
								color: "rgba(255, 255, 255, 0.9)",
							}}>
							We're sorry, but your account has been removed from our system
						</Typography>
					</Box>

					<CardContent sx={{ padding: 4 }}>
						<Stack spacing={3}>
							<Box>
								<Typography
									variant="body1"
									color="text.secondary"
									sx={{ mb: 2, lineHeight: 1.7 }}>
									Your account was deleted and is no longer accessible. If you
									believe this was done in error, please contact our support
									team for assistance.
								</Typography>

								{deletedAt && (
									<Box
										sx={{
											bgcolor: "grey.100",
											p: 2,
											borderRadius: 1,
											mb: 2,
										}}>
										<Typography variant="body2" color="text.secondary">
											<strong>Deleted on:</strong> {deletedAt}
										</Typography>
									</Box>
								)}

								{reason && (
									<Box
										sx={{
											bgcolor: "grey.100",
											p: 2,
											borderRadius: 1,
										}}>
										<Typography variant="body2" color="text.secondary">
											<strong>Reason:</strong> {reason}
										</Typography>
									</Box>
								)}
							</Box>

							<Stack spacing={2}>
								<Button
									variant="contained"
									size="large"
									fullWidth
									startIcon={<SupportAgentIcon />}
									sx={{
										background:
											"linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
										py: 1.5,
										"&:hover": {
											background:
												"linear-gradient(135deg, #5568d3 0%, #65408b 100%)",
										},
									}}
									component="a"
									href="mailto:support@forus.com?subject=Account Deletion Appeal">
									Contact Support
								</Button>

								<Button
									variant="outlined"
									size="large"
									fullWidth
									startIcon={<HomeIcon />}
									onClick={() => navigate("/")}
									sx={{
										py: 1.5,
										borderColor: "grey.400",
										color: "text.primary",
										"&:hover": {
											borderColor: "grey.600",
											bgcolor: "grey.50",
										},
									}}>
									Return to Home
								</Button>
							</Stack>

							<Box sx={{ textAlign: "center", pt: 2 }}>
								<Typography variant="body2" color="text.secondary">
									Need help?{" "}
									<MuiLink
										href="mailto:support@forus.com"
										sx={{
											color: "primary.main",
											textDecoration: "none",
											"&:hover": {
												textDecoration: "underline",
											},
										}}>
										support@forus.com
									</MuiLink>
								</Typography>
							</Box>
						</Stack>
					</CardContent>
				</Card>
			</Container>
		</Box>
	);
};

export default AccountDeleted;
